/**
 * AddCommentModal Component
 * Modal for adding a manual comment on selected text.
 */
import React, { useState, useRef, useEffect } from 'react';
import type { Position } from '../../types';

interface AddCommentModalProps {
  isOpen: boolean;
  highlightedText: string;
  pageNumber: number;
  position: Position;
  onSave: (
    suggestion: string,
    category: string
  ) => Promise<void>;
  onClose: () => void;
}

const CATEGORIES = [
  { value: 'clarity', label: 'Clarity' },
  { value: 'grammar', label: 'Grammar' },
  { value: 'citation', label: 'Citation' },
  { value: 'structure', label: 'Structure' },
  { value: 'content', label: 'Content' },
  { value: 'style', label: 'Style' },
  { value: 'other', label: 'Other' },
];

const AddCommentModal: React.FC<AddCommentModalProps> = ({
  isOpen,
  highlightedText,
  pageNumber,
  onSave,
  onClose,
}) => {
  const [suggestion, setSuggestion] = useState('');
  const [category, setCategory] = useState('clarity');
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  // Reset state when modal opens with new selection
  useEffect(() => {
    if (isOpen) {
      setSuggestion('');
      setCategory('clarity');
    }
  }, [isOpen, highlightedText]);

  const handleSave = async () => {
    if (!suggestion.trim()) return;

    setIsSaving(true);
    try {
      await onSave(suggestion.trim(), category);
      onClose();
    } catch {
      // Error is handled by the hook
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 animate-backdrop-enter"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden animate-modal-enter"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Add Comment</h3>
          <p className="text-sm text-gray-500 mt-1">
            Page {pageNumber}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Highlighted text */}
          <div className="p-3 bg-yellow-50 rounded-md border border-yellow-200">
            <p className="text-sm font-medium text-gray-700 mb-1">
              Selected text:
            </p>
            <p className="text-sm text-gray-900 italic">
              &quot;{highlightedText.slice(0, 200)}
              {highlightedText.length > 200 ? '...' : ''}&quot;
            </p>
          </div>

          {/* Category selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Comment input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Comment
            </label>
            <textarea
              ref={textareaRef}
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              placeholder="Enter your feedback comment..."
            />
          </div>

          <p className="text-sm text-gray-500">
            Manual comments are automatically accepted and help inform the AI for future suggestions.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Ctrl+Enter</kbd>{' '}
            to save
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !suggestion.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Adding...' : 'Add Comment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCommentModal;
